import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useCloudProjectsStore = create(
  persist(
    (set, get) => ({
      cloudProviders: [
        {
          id: 'GCP-001',
          provider: 'google_cloud',
          name: 'Google Cloud',
          icon: '☁️',
          color: 'bg-blue-100 text-blue-800',
          projects: [
            {
              id: 'GCP-PROD',
              name: 'Production API',
              status: 'running',
              uptime: 99.97,
              resources: { cpu: 45, memory: 62, storage: 78 },
              cost: 2840.50,
              instances: 12,
              alerts: [],
              aiRecommendations: [],
              created: new Date(Date.now() - 90 * 24 * 60 * 60000),
              updated: new Date(Date.now() - 1 * 60000),
            },
            {
              id: 'GCP-DEV',
              name: 'Development',
              status: 'running',
              uptime: 99.82,
              resources: { cpu: 28, memory: 35, storage: 45 },
              cost: 450.20,
              instances: 3,
              alerts: [],
              aiRecommendations: [],
              created: new Date(Date.now() - 60 * 24 * 60 * 60000),
              updated: new Date(Date.now() - 5 * 60000),
            },
          ],
        },
        {
          id: 'AWS-001',
          provider: 'aws',
          name: 'Amazon AWS',
          icon: '🟠',
          color: 'bg-orange-100 text-orange-800',
          projects: [
            {
              id: 'AWS-WEB',
              name: 'Web Services',
              status: 'running',
              uptime: 99.99,
              resources: { cpu: 52, memory: 68, storage: 85 },
              cost: 3200.75,
              instances: 15,
              alerts: [],
              aiRecommendations: [],
              created: new Date(Date.now() - 120 * 24 * 60 * 60000),
              updated: new Date(Date.now() - 2 * 60000),
            },
            {
              id: 'AWS-DB',
              name: 'Database Cluster',
              status: 'running',
              uptime: 99.98,
              resources: { cpu: 72, memory: 88, storage: 95 },
              cost: 2150.30,
              instances: 6,
              alerts: [],
              aiRecommendations: [],
              created: new Date(Date.now() - 100 * 24 * 60 * 60000),
              updated: new Date(Date.now() - 3 * 60000),
            },
          ],
        },
        {
          id: 'AZURE-001',
          provider: 'azure',
          name: 'Microsoft Azure',
          icon: '◆',
          color: 'bg-cyan-100 text-cyan-800',
          projects: [
            {
              id: 'AZURE-APP',
              name: 'App Services',
              status: 'running',
              uptime: 99.93,
              resources: { cpu: 38, memory: 52, storage: 71 },
              cost: 1540.60,
              instances: 7,
              alerts: [],
              aiRecommendations: [],
              created: new Date(Date.now() - 75 * 24 * 60 * 60000),
              updated: new Date(Date.now() - 4 * 60000),
            },
          ],
        },
      ],

      // Add new cloud project
      addCloudProject: (providerId, projectData) => {
        set((state) => ({
          cloudProviders: state.cloudProviders.map((provider) =>
            provider.id === providerId
              ? {
                  ...provider,
                  projects: [
                    ...provider.projects,
                    {
                      id: `${providerId.split('-')[0]}-${Date.now()}`,
                      ...projectData,
                      alerts: [],
                      aiRecommendations: [],
                      created: new Date(),
                      updated: new Date(),
                    },
                  ],
                }
              : provider
          ),
        }))
      },

      // Update cloud project
      updateCloudProject: (providerId, projectId, updates) => {
        set((state) => ({
          cloudProviders: state.cloudProviders.map((provider) =>
            provider.id === providerId
              ? {
                  ...provider,
                  projects: provider.projects.map((project) =>
                    project.id === projectId
                      ? { ...project, ...updates, updated: new Date() }
                      : project
                  ),
                }
              : provider
          ),
        }))
      },

      // Delete cloud project
      deleteCloudProject: (providerId, projectId) => {
        set((state) => ({
          cloudProviders: state.cloudProviders.map((provider) =>
            provider.id === providerId
              ? {
                  ...provider,
                  projects: provider.projects.filter((p) => p.id !== projectId),
                }
              : provider
          ),
        }))
      },

      // Add AI recommendation
      addAIRecommendation: (providerId, projectId, recommendation) => {
        set((state) => ({
          cloudProviders: state.cloudProviders.map((provider) =>
            provider.id === providerId
              ? {
                  ...provider,
                  projects: provider.projects.map((project) =>
                    project.id === projectId
                      ? {
                          ...project,
                          aiRecommendations: [
                            ...project.aiRecommendations,
                            { ...recommendation, id: Date.now(), date: new Date() },
                          ],
                        }
                      : project
                  ),
                }
              : provider
          ),
        }))
      },

      // Add alert
      addAlert: (providerId, projectId, alert) => {
        set((state) => ({
          cloudProviders: state.cloudProviders.map((provider) =>
            provider.id === providerId
              ? {
                  ...provider,
                  projects: provider.projects.map((project) =>
                    project.id === projectId
                      ? {
                          ...project,
                          alerts: [...project.alerts, { ...alert, id: Date.now(), date: new Date() }],
                        }
                      : project
                  ),
                }
              : provider
          ),
        }))
      },

      // Get all projects
      getAllProjects: () => {
        return get().cloudProviders.flatMap((provider) =>
          provider.projects.map((project) => ({ ...project, provider: provider.name }))
        )
      },

      // Get projects by provider
      getProjectsByProvider: (providerId) => {
        const provider = get().cloudProviders.find((p) => p.id === providerId)
        return provider ? provider.projects : []
      },

      // Get project by ID
      getProjectById: (providerId, projectId) => {
        const provider = get().cloudProviders.find((p) => p.id === providerId)
        return provider?.projects.find((p) => p.id === projectId)
      },

      // Generate AI insights based on resource usage
      generateAIInsights: (providerId, projectId) => {
        const project = get().getProjectById(providerId, projectId)
        if (!project) return []

        const insights = []

        if (project.resources.cpu > 80) {
          insights.push({
            type: 'warning',
            title: 'High CPU Usage',
            message: `CPU usage is at ${project.resources.cpu}%. Consider scaling up or optimizing application code.`,
            priority: 'high',
          })
        }

        if (project.resources.memory > 85) {
          insights.push({
            type: 'warning',
            title: 'High Memory Usage',
            message: `Memory usage is at ${project.resources.memory}%. Check for memory leaks or increase instance memory.`,
            priority: 'high',
          })
        }

        if (project.resources.storage > 90) {
          insights.push({
            type: 'critical',
            title: 'Storage Nearly Full',
            message: `Storage is at ${project.resources.storage}%. Clean up old logs/backups or increase storage capacity.`,
            priority: 'critical',
          })
        }

        if (project.cost > 3000) {
          insights.push({
            type: 'info',
            title: 'High Cost Project',
            message: `This project costs $${project.cost.toFixed(2)}/month. Review instance sizing and unused resources.`,
            priority: 'medium',
          })
        }

        if (project.uptime < 99.9) {
          insights.push({
            type: 'warning',
            title: 'Below Target Uptime',
            message: `Current uptime is ${project.uptime}%. Investigate recent downtime and improve reliability.`,
            priority: 'high',
          })
        }

        if (project.instances > 10) {
          insights.push({
            type: 'info',
            title: 'Consider Load Balancing',
            message: `With ${project.instances} instances, ensure proper load balancing and auto-scaling policies.`,
            priority: 'low',
          })
        }

        return insights
      },

      // Get cost optimization suggestions
      getCostOptimizations: () => {
        const allProjects = get().getAllProjects()
        const suggestions = []

        const totalCost = allProjects.reduce((sum, p) => sum + p.cost, 0)
        const avgCost = totalCost / allProjects.length

        allProjects.forEach((project) => {
          if (project.cost > avgCost * 1.5) {
            suggestions.push({
              project: project.name,
              suggestion: `${project.name} costs 50% more than average. Consider right-sizing instances.`,
              potentialSavings: project.cost * 0.15,
            })
          }

          if (project.resources.cpu < 20 && project.instances > 1) {
            suggestions.push({
              project: project.name,
              suggestion: `${project.name} is underutilized (${project.resources.cpu}% CPU). Consolidate instances.`,
              potentialSavings: project.cost * 0.25,
            })
          }
        })

        return suggestions
      },
    }),
    {
      name: 'cloud-projects-storage',
    }
  )
)
